/**
 * =============================================================================
 * LAYOUT COMPONENT - Global App Shell
 * =============================================================================
 * 
 * This component wraps ALL pages in the application and provides:
 * - Consistent header navigation across the entire app
 * - Consistent footer across all pages
 * - Global authentication state management
 * - Responsive layout structure (flexbox column layout)
 * 
 * ARCHITECTURE:
 * ┌─────────────────────────────────────┐
 * │            Header                   │  ← Sticky navigation bar
 * ├─────────────────────────────────────┤
 * │                                     │
 * │            Main Content             │  ← Page-specific content (children)
 * │           (flex-grow)               │
 * │                                     │
 * ├─────────────────────────────────────┤
 * │            Footer                   │  ← Site-wide footer
 * └─────────────────────────────────────┘
 * 
 * USAGE:
 * This component is used in App.tsx to wrap all routes.
 * Individual pages should NOT import Header or Footer directly.
 * 
 * EXTENDING:
 * To add new global UI elements (e.g., notification banners, cookie consent):
 * 1. Import the component here
 * 2. Add it to the render tree in the appropriate position
 * 
 * @see src/App.tsx - Where Layout is used
 * @see src/components/Header.tsx - Navigation component
 * @see src/components/Footer.tsx - Footer component
 * =============================================================================
 */

import { ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { PrivateRouteRobots } from "@/components/PrivateRouteRobots";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { usePageTracking } from "@/hooks/usePageTracking";

// -----------------------------------------------------------------------------
// Type Definitions
// -----------------------------------------------------------------------------

interface LayoutProps {
  /** The page content to render between Header and Footer */
  children: ReactNode;
}

// -----------------------------------------------------------------------------
// Routes Configuration
// -----------------------------------------------------------------------------

/**
 * Routes that should show a back button in the header.
 * These are typically detail pages or pages accessed from other pages.
 */
const ROUTES_WITH_BACK_BUTTON = [
  '/profile',
  '/settings',
  '/account',
  '/listing/',
  '/chat/',
  '/edit-listing/',
  '/create-listing',
  '/saved',
  '/messages',
  '/dashboard',
  '/purchases',
  '/user/',
  '/review/',
  '/contact',
  '/terms-of-use',
  '/fraktvillkor',
  '/buyer-protection',
  '/privacy-policy',
  '/request-password-reset',
  '/reset-password',
  // Admin routes
  '/admin',
  '/security-monitor',
];

// -----------------------------------------------------------------------------
// Layout Component
// -----------------------------------------------------------------------------

/**
 * Global layout wrapper that includes Header and Footer on ALL pages.
 * This component handles auth state globally so individual pages don't need to.
 * 
 * @param children - The page content to render
 * @returns The complete page layout with header, content, and footer
 */
export const Layout = ({ children }: LayoutProps) => {
  // ---------------------------------------------------------------------------
  // State Management
  // ---------------------------------------------------------------------------
  
  /**
   * Current authenticated user state
   * - null = not logged in
   * - User object = logged in
   */
  const [user, setUser] = useState<any>(null);
  
  const location = useLocation();
  
  // Anonymous page view tracking (GDPR-compliant)
  usePageTracking();

  // ---------------------------------------------------------------------------
  // Back Button Logic
  // ---------------------------------------------------------------------------
  
  /**
   * Determine if the current route should show a back button.
   * This provides native-like navigation on detail and sub-pages.
   */
  const shouldShowBackButton = ROUTES_WITH_BACK_BUTTON.some(route => 
    location.pathname.startsWith(route)
  );

  // ---------------------------------------------------------------------------
  // Authentication Subscription
  // ---------------------------------------------------------------------------
  
  useEffect(() => {
    /**
     * Get the initial session when the component mounts.
     * This ensures we have the correct auth state on page load.
     */
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    /**
     * Subscribe to auth state changes (login, logout, token refresh).
     * This keeps the user state in sync across the entire application.
     */
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });

    // Cleanup subscription on component unmount
    return () => subscription.unsubscribe();
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* 
        Header: Sticky navigation bar
        Receives user prop to show appropriate navigation items
        (logged in vs logged out state)
        showBackButton is determined by current route
      */}
      <Header user={user} showBackButton={shouldShowBackButton} />
      
      {/* 
        Main Content Area: Grows to fill available space
        This ensures footer stays at bottom even with minimal content
      */}
      <PrivateRouteRobots />
      <main className="flex-1">
        {children}
      </main>
      
      {/* 
        Footer: Contains site links, legal pages, and branding
        Uses mt-auto in its styling to stay at bottom
      */}
      <Footer />
    </div>
  );
};
